import { z } from "zod";
import type {
  ContentType,
  DisplayCondition,
  DisplayFormType,
  DisplayImage,
  MultimediaItem,
} from "../types";

export const defaultDisplayValues: DisplayFormType = {
  id: "",
  propertyTicker: "",
  active: false,
  defaultOption: false,
  position: undefined,
  ticker: "",
  type: "CUSTOM",
  startDate: undefined,
  endDate: undefined,
  travelWindow: undefined,
  entityFilter: "PROPERTY",
  tickerFilter: undefined,
  contentType: "HTML" as ContentType,
  youTubeURL: undefined,
  translationsList: [],
  multimedia: [],
  imageManager: [],
  originalMultimedia: [],
  conditions: [],
};

const YOUTUBE_REGEX = /^(https?:\/\/)?(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)[\w-]{11}(\S+)?$/;
const TICKER_CSV_REGEX = /^[A-Za-z0-9_-]+(\s*,\s*[A-Za-z0-9_-]+)*$/;

const translationSchema = z.object({
  id: z.string().optional(),
  locale: z.string(),
  name: z.string(),
  description: z.string().optional(),
});

const travelWindowSchema = z.object({
  startDate: z.string().nullable().optional(),
  endDate: z.string().nullable().optional(),
});

export const displaySchema = z.object({
  id: z.string().optional(),
  propertyTicker: z.string().trim().min(1, "Required"),
  ticker: z.string().trim().min(1, "Required"),
  active: z.boolean(),
  defaultOption: z.boolean(),
  position: z.number().optional(),
  type: z.enum(["INFO", "AVAILABILITY", "MAP", "CUSTOM"]).optional(),
  label: z.string().optional(),
  content: z.string().optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  visible: z.string().optional(),
  travelWindow: travelWindowSchema.optional(),
  entityFilter: z.enum(["PROPERTY", "ACCOMMODATION"]),
  tickerFilter: z.string().trim().optional(),
  contentType: z.enum(["HTML", "VIDEO", "IMAGE"]),
  youTubeURL: z.string().optional(),
  translationsList: z.array(translationSchema).optional(),
  multimedia: z.array(z.custom<MultimediaItem>()).optional(),
  imageManager: z.array(z.custom<DisplayImage>()).optional(),
  originalMultimedia: z.array(z.custom<MultimediaItem>()).optional(),
  multimediaList: z.array(z.custom<DisplayImage>()).optional(),
  conditions: z.array(z.custom<DisplayCondition>()).optional(),
});

export const refinedDisplaySchema = displaySchema
  // Dates
  .refine(data => !(data.startDate && data.endDate && data.startDate > data.endDate), {
    message: "End date must be after start date",
    path: ["endDate"],
  })
  // Video validation
  .refine(
    data => data.contentType !== "VIDEO" || (data.youTubeURL && data.youTubeURL.trim().length > 0),
    { message: "YouTube URL required", path: ["youTubeURL"] }
  )
  .refine(
    data =>
      data.contentType !== "VIDEO" ||
      !data.youTubeURL ||
      YOUTUBE_REGEX.test(data.youTubeURL.trim()),
    { message: "Invalid YouTube URL", path: ["youTubeURL"] }
  )
  // Image validation
  .refine(
    data =>
      data.contentType !== "IMAGE" ||
      (data.multimedia?.length ?? 0) + (data.imageManager?.length ?? 0) > 0,
    { message: "You must add at least one image", path: ["imageManager"] }
  )
  // tickerFilter validation
  .refine(data => data.entityFilter !== "PROPERTY" || !data.tickerFilter?.trim(), {
    message: "Should not be set when filter is PROPERTY",
    path: ["tickerFilter"],
  })
  .refine(
    data =>
      data.entityFilter === "PROPERTY" ||
      !data.tickerFilter ||
      TICKER_CSV_REGEX.test(data.tickerFilter),
    { message: "Invalid tickers CSV", path: ["tickerFilter"] }
  )
  // translations validation
  .refine(
    data =>
      (data.translationsList ?? []).every(
        t =>
          (t.name === undefined || t.name.trim() !== "") &&
          (t.description === undefined || t.description.trim() !== "")
      ),
    { message: "Required if translation is included", path: ["translationsList"] }
  );
